/**
 * Placing calls (S07).
 *
 * The result type is a discriminated union rather than a boolean plus a message,
 * because the refusals are not interchangeable and the UI has to say something
 * different for each. "Could not place the call" is the answer that teaches an
 * operator nothing and makes them try again for no reason.
 */
import { z } from 'zod';

export const instantCallSchema = z.object({
  contactIds: z.array(z.string().uuid()).min(1).max(50),
});
export type InstantCall = z.infer<typeof instantCallSchema>;

/**
 * Why one contact was not dialled.
 *
 * `UNCERTAIN` is the one that matters: the request may have reached the platform
 * and the phone may be ringing, so it is neither a success to celebrate nor a
 * failure to retry. Collapsing it into either would eventually ring somebody
 * twice.
 */
export const DIAL_REFUSAL = [
  'NOT_FOUND',
  'DO_NOT_CALL',
  'OUTSIDE_HOURS',
  'IN_CALL',
  'AT_CAPACITY',
  'REFUSED',
  'UNCERTAIN',
] as const;
export const dialRefusalSchema = z.enum(DIAL_REFUSAL);
export type DialRefusal = z.infer<typeof dialRefusalSchema>;

/** What the UI says for each. Defined here so both sides agree on the wording. */
export const dialRefusalMessages: Record<DialRefusal, string> = {
  NOT_FOUND: 'no longer exists',
  DO_NOT_CALL: 'is on the do-not-call list',
  OUTSIDE_HOURS: 'is outside calling hours',
  IN_CALL: 'already has a call in progress',
  AT_CAPACITY: 'could not start — all call slots are busy',
  REFUSED: 'was refused by the calling platform',
  UNCERTAIN: 'may already be ringing — check before trying again',
};

/**
 * The same reasons phrased for a count rather than a named subject.
 *
 * Two maps rather than one, because `"2 is on the do-not-call list"` is the
 * sentence you get from reusing the singular form, and deriving one from the
 * other means rewriting English with a regular expression. Spelling both out is
 * duller and always correct.
 *
 * Every phrase here is a count-neutral noun phrase, never a verb phrase: the
 * count in front of it varies, so 'no longer exist' reads correctly at two and
 * wrongly at one. There is no count at which a noun phrase is wrong.
 */
export const dialRefusalGroupPhrases: Record<DialRefusal, string> = {
  NOT_FOUND: 'no longer in the list',
  DO_NOT_CALL: 'on the do-not-call list',
  OUTSIDE_HOURS: 'outside calling hours',
  IN_CALL: 'already in a call',
  AT_CAPACITY: 'waiting for a free call slot',
  REFUSED: 'refused by the calling platform',
  UNCERTAIN: 'possibly already ringing',
};
export const dialAttemptSchema = z.object({
  contactId: z.string().uuid(),
  name: z.string().nullable(),
  placed: z.boolean(),
  callId: z.string().uuid().nullable(),
  reason: dialRefusalSchema.nullable(),
  /** Set only for OUTSIDE_HOURS, so the UI can say when it reopens. */
  nextOpenAt: z.string().datetime().nullable(),
});
export type DialAttempt = z.infer<typeof dialAttemptSchema>;

export const instantCallResultSchema = z.object({
  placed: z.number().int().min(0),
  attempts: z.array(dialAttemptSchema),
});
export type InstantCallResult = z.infer<typeof instantCallResultSchema>;
